import './styles.css';
import {
  createInsight,
  defaultFilters,
  displayCampaign,
  filterSegments,
  filtersToSearch,
  formatMoney,
  formatPercent,
  freshnessFromDate,
  isDashboardSnapshot,
  segmentStatus,
  summarizeSegments,
} from './model';
import type { DashboardFilters, DashboardSegment, DashboardSnapshot, SegmentSort } from './types';

const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('App root not found');
const app: HTMLDivElement = appElement;

let snapshot: DashboardSnapshot;
let filters = defaultFilters(window.location.search);
let dataOrigin: 'api' | 'fallback' = 'fallback';

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[char] ?? char);
}

function uniqueValues(key: 'medium' | 'source'): string[] {
  return [...new Set(snapshot.segments.map((segment) => segment[key]))]
    .sort((a, b) => a.localeCompare(b, 'uk-UA'));
}

function optionList(values: string[], current: string, allLabel: string): string {
  return [`<option value="all">${allLabel}</option>`]
    .concat(values.map((value) => `<option value="${escapeHtml(value)}"${value === current ? ' selected' : ''}>${escapeHtml(value)}</option>`))
    .join('');
}

function renderShell(): void {
  const freshness = freshnessFromDate(snapshot.sourceLastUpdatedAt) === 'stale' || dataOrigin === 'fallback' ? 'stale' : snapshot.freshness.state;
  const freshnessLabel = freshness === 'live' ? 'Дані актуальні' : 'Дані застарілі';
  const sourceDate = new Intl.DateTimeFormat('uk-UA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Kiev' })
    .format(new Date(snapshot.sourceLastUpdatedAt));

  app.innerHTML = `
    <header class="site-header">
      <div class="page-width header-inner">
        <div>
          <p class="eyebrow">Cliniccards × SalesDrive</p>
          <h1>LTV пацієнтів</h1>
          <p class="subtitle">Аналіз каналів залучення <span aria-hidden="true">·</span> ${escapeHtml(snapshot.periodLabel)} <span aria-hidden="true">·</span> ${snapshot.segments.length} сегментів</p>
        </div>
        <div class="source-stack">
          <span class="source-link" title="Джерело приховане у публічній версії">
            <span class="source-dot" aria-hidden="true"></span> Захищений зріз
          </span>
          <span class="freshness freshness--${freshness}" title="${escapeHtml(snapshot.freshness.message)}">
            ${freshnessLabel} · ${escapeHtml(sourceDate)}
          </span>
        </div>
      </div>
    </header>
    <main id="main-content" class="page-width">
      <section class="filter-panel" aria-label="Фільтри звіту">
        <div class="filter-field filter-field--search">
          <label for="filter-query">Пошук кампанії</label>
          <input id="filter-query" type="search" value="${escapeHtml(filters.query)}" placeholder="Наприклад, pmax_local" autocomplete="off" />
        </div>
        <div class="filter-field">
          <label for="filter-medium">Канал</label>
          <select id="filter-medium">${optionList(uniqueValues('medium'), filters.medium, 'Усі канали')}</select>
        </div>
        <div class="filter-field">
          <label for="filter-source">Джерело</label>
          <select id="filter-source">${optionList(uniqueValues('source'), filters.source, 'Усі джерела')}</select>
        </div>
        <div class="filter-field">
          <label for="filter-sort">Сортування</label>
          <select id="filter-sort">
            ${sortOption('revenue', 'За виручкою')}
            ${sortOption('lifetimeLtv', 'За LTV')}
            ${sortOption('patients', 'За пацієнтами')}
            ${sortOption('conversion', 'За конверсією')}
            ${sortOption('ltvGrowth', 'За приростом LTV')}
          </select>
        </div>
        <button id="reset-filters" class="reset-button" type="button">Скинути</button>
      </section>

      <div id="dashboard-content"></div>
    </main>
    <footer class="page-width footer">
      <p><strong>Методика.</strong> ${escapeHtml(snapshot.definitions.attribution)}</p>
      <p><strong>Повторний пацієнт.</strong> ${escapeHtml(snapshot.definitions.returningPatient)}</p>
      <p><strong>Повторна виручка.</strong> ${escapeHtml(snapshot.definitions.repeatRevenue)}</p>
    </footer>
  `;

  bindControls();
  renderDashboard();
}

function sortOption(value: SegmentSort, label: string): string {
  return `<option value="${value}"${filters.sort === value ? ' selected' : ''}>${label}</option>`;
}

function bindControls(): void {
  const query = document.querySelector<HTMLInputElement>('#filter-query');
  const medium = document.querySelector<HTMLSelectElement>('#filter-medium');
  const source = document.querySelector<HTMLSelectElement>('#filter-source');
  const sort = document.querySelector<HTMLSelectElement>('#filter-sort');
  const reset = document.querySelector<HTMLButtonElement>('#reset-filters');

  query?.addEventListener('input', () => updateFilters({ query: query.value }));
  medium?.addEventListener('change', () => updateFilters({ medium: medium.value }));
  source?.addEventListener('change', () => updateFilters({ source: source.value }));
  sort?.addEventListener('change', () => updateFilters({ sort: sort.value as SegmentSort }));
  reset?.addEventListener('click', () => {
    filters = defaultFilters('');
    window.history.replaceState(null, '', window.location.pathname);
    renderShell();
  });
}

function updateFilters(next: Partial<DashboardFilters>): void {
  filters = { ...filters, ...next };
  window.history.replaceState(null, '', `${window.location.pathname}${filtersToSearch(filters)}`);
  renderDashboard();
}

function renderDashboard(): void {
  const container = document.querySelector<HTMLDivElement>('#dashboard-content');
  if (!container) return;

  const rows = filterSegments(snapshot.segments, filters);
  const summary = summarizeSegments(rows, snapshot.summary);
  const activeFilters = filters.medium !== 'all' || filters.source !== 'all' || filters.query.trim();
  const returningValue = activeFilters ? '—' : formatPercent(snapshot.summary.returningRate);
  const returningMeta = activeFilters
    ? 'показник доступний для повного звіту'
    : `${snapshot.summary.returningPatients} з ${snapshot.summary.patients} пацієнтів`;

  container.innerHTML = `
    <section class="kpi-grid" aria-label="Ключові показники">
      ${kpiCard('Пацієнти', formatMoney(summary.patients), `з ${formatMoney(summary.leads)} лідів · конв. ${formatPercent(summary.conversion)}`)}
      ${kpiCard('Виручка', `${formatMoney(summary.revenue)} ₴`, `${summary.campaignsWithRevenue} кампаній з виручкою`)}
      ${kpiCard('Середній LTV', `${formatMoney(summary.lifetimeLtv)} ₴`, 'чиста виручка / пацієнт')}
      ${kpiCard('Повертаються', returningValue, returningMeta, 'accent')}
    </section>

    <section class="insight-card" aria-labelledby="insight-title">
      <p id="insight-title" class="insight-label">Головне</p>
      <p>${escapeHtml(createInsight(rows, summary))}</p>
    </section>

    <section class="section-block" aria-labelledby="segments-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Порівняння та ранжування</p>
          <h2 id="segments-title">Канали залучення</h2>
        </div>
        <p class="section-meta">Показано ${rows.length} з ${snapshot.segments.length} · ${sortLabel(filters.sort)}</p>
      </div>
      ${rows.length ? renderTable(rows) : renderEmptyState()}
      ${rows.length ? renderMobileCards(rows) : ''}
    </section>

    <section class="chart-grid" aria-label="Рейтинг кампаній">
      ${renderBarPanel('Виручка за кампаніями', rows, 'revenue', '₴', '#173f4c')}
      ${renderBarPanel('Середній LTV на пацієнта', rows.filter((row) => row.patients > 0), 'lifetimeLtv', '₴', '#b88942')}
    </section>

    <section class="secondary-metrics" aria-label="Додаткові показники">
      <div><span>Повторна виручка</span><strong>${formatMoney(summary.repeatRevenue)} ₴</strong></div>
      <div><span>Сегменти зі зростанням LTV</span><strong>${summary.campaignsWithRepeatRevenue}</strong></div>
      <div><span>Виручка без атрибуції</span><strong>${formatPercent(unattributedShare(rows, summary.revenue))}</strong></div>
    </section>
  `;
}

function kpiCard(label: string, value: string, meta: string, variant = ''): string {
  return `<article class="kpi-card${variant ? ` kpi-card--${variant}` : ''}">
    <p>${escapeHtml(label)}</p><strong>${escapeHtml(value)}</strong><span>${escapeHtml(meta)}</span>
  </article>`;
}

function renderTable(rows: DashboardSegment[]): string {
  return `<div class="table-wrap">
    <table>
      <thead><tr>
        <th scope="col">Канал / кампанія</th><th scope="col" class="numeric">Ліди</th><th scope="col" class="numeric">Пацієнти</th>
        <th scope="col">Конв.</th><th scope="col" class="numeric">Виручка</th><th scope="col" class="numeric">LTV-30</th>
        <th scope="col" class="numeric">LTV-365</th><th scope="col" class="numeric">Δ LTV</th><th scope="col">Тип</th>
      </tr></thead>
      <tbody>${rows.map(renderTableRow).join('')}</tbody>
      <tfoot><tr><th scope="row">Разом у вибірці</th><td class="numeric">${formatMoney(rows.reduce((sum, row) => sum + row.leads, 0))}</td>
        <td class="numeric">${formatMoney(rows.reduce((sum, row) => sum + row.patients, 0))}</td><td>${formatPercent(summaryConversion(rows))}</td>
        <td class="numeric emphasized">${formatMoney(rows.reduce((sum, row) => sum + row.revenue, 0))}</td><td></td><td></td>
        <td class="numeric positive">+${formatMoney(rows.reduce((sum, row) => sum + row.repeatRevenue, 0))}</td><td></td></tr></tfoot>
    </table>
  </div>`;
}

function renderTableRow(row: DashboardSegment): string {
  const status = segmentStatus(row);
  const statusText = { growth: 'Повертається', revenue: 'Разовий', 'no-patients': 'Немає', 'zero-value': 'Без оплат' }[status];
  return `<tr>
    <th scope="row"><span class="channel-name">${escapeHtml(row.medium)}</span><span class="campaign-name">${escapeHtml(row.source)} · ${escapeHtml(row.campaign)}</span></th>
    <td class="numeric">${formatMoney(row.leads)}</td><td class="numeric">${formatMoney(row.patients)}</td>
    <td><span class="conversion-cell"><i style="--fill:${Math.max(0, Math.min(100, row.conversion * 100))}%"></i>${formatPercent(row.conversion)}</span></td>
    <td class="numeric">${formatMoney(row.revenue)}</td><td class="numeric">${row.patients ? formatMoney(row.ltv30) : '—'}</td>
    <td class="numeric">${row.patients ? formatMoney(row.ltv365) : '—'}</td>
    <td class="numeric ${row.ltvGrowth > 0 ? 'positive' : ''}">${row.ltvGrowth > 0 ? '+' : ''}${formatMoney(row.ltvGrowth)}</td>
    <td><span class="status-badge status-badge--${status}">${statusText}</span></td>
  </tr>`;
}

function renderMobileCards(rows: DashboardSegment[]): string {
  return `<div class="segment-cards">${rows.map((row) => {
    const status = segmentStatus(row);
    return `<article class="segment-card">
      <div class="segment-card__title"><div><span>${escapeHtml(row.medium)} · ${escapeHtml(row.source)}</span><h3>${escapeHtml(displayCampaign(row))}</h3></div><span class="status-dot status-dot--${status}" aria-hidden="true"></span></div>
      <dl><div><dt>Пацієнти</dt><dd>${row.patients} / ${row.leads}</dd></div><div><dt>Конверсія</dt><dd>${formatPercent(row.conversion)}</dd></div>
        <div><dt>Виручка</dt><dd>${formatMoney(row.revenue)} ₴</dd></div><div><dt>Lifetime LTV</dt><dd>${formatMoney(row.lifetimeLtv)} ₴</dd></div></dl>
      <div class="card-growth"><span>LTV-30 → LTV-365</span><strong>${formatMoney(row.ltv30)} → ${formatMoney(row.ltv365)} ₴</strong></div>
    </article>`;
  }).join('')}</div>`;
}

function renderBarPanel(title: string, rows: DashboardSegment[], key: 'revenue' | 'lifetimeLtv', suffix: string, color: string): string {
  const top = [...rows].filter((row) => row[key] > 0).sort((a, b) => b[key] - a[key]).slice(0, 10);
  const max = top[0]?.[key] || 1;
  return `<article class="chart-panel"><div class="chart-heading"><h2>${escapeHtml(title)}</h2><span>топ-${top.length}</span></div>
    <div class="bar-list">${top.map((row) => `<div class="bar-row">
      <span class="bar-label" title="${escapeHtml(displayCampaign(row))}">${escapeHtml(displayCampaign(row))}</span>
      <span class="bar-track"><i style="width:${Math.max(1.5, row[key] / max * 100)}%;background:${color}"></i></span>
      <strong>${formatMoney(row[key])} ${suffix}</strong></div>`).join('')}</div></article>`;
}

function renderEmptyState(): string {
  return `<div class="empty-state"><strong>Нічого не знайдено</strong><p>Змініть пошук або скиньте фільтри.</p></div>`;
}

function summaryConversion(rows: DashboardSegment[]): number {
  const leads = rows.reduce((sum, row) => sum + row.leads, 0);
  const patients = rows.reduce((sum, row) => sum + row.patients, 0);
  return leads ? patients / leads : 0;
}

function unattributedShare(rows: DashboardSegment[], revenue: number): number {
  if (!revenue) return 0;
  const unattributed = rows.find((row) => row.medium === '(не передано)' && row.source === '(не передано)');
  return (unattributed?.revenue || 0) / revenue;
}

function sortLabel(sort: SegmentSort): string {
  return ({ revenue: 'за виручкою', lifetimeLtv: 'за LTV', patients: 'за пацієнтами', conversion: 'за конверсією', ltvGrowth: 'за приростом LTV' })[sort];
}

async function loadSnapshot(): Promise<DashboardSnapshot> {
  const tryLoad = async (url: string): Promise<DashboardSnapshot> => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Dashboard source returned ${response.status}`);
    const value: unknown = await response.json();
    if (!isDashboardSnapshot(value)) throw new Error('Dashboard schema is invalid');
    return normalizeSnapshot(value);
  };

  try {
    const value = await tryLoad('/api/dashboard');
    dataOrigin = 'api';
    return value;
  } catch {
    dataOrigin = 'fallback';
    return tryLoad('/data/dashboard.json');
  }
}

function normalizeSnapshot(value: DashboardSnapshot): DashboardSnapshot {
  const segments = value.segments.map((segment) => ({
    ...segment,
    conversion: segment.leads ? segment.patients / segment.leads : 0,
  }));

  return {
    ...value,
    summary: {
      ...value.summary,
      conversion: value.summary.leads ? value.summary.patients / value.summary.leads : 0,
    },
    segments,
  };
}

app.innerHTML = `<main class="loading-state"><span class="loading-mark" aria-hidden="true"></span><p>Завантажуємо LTV-звіт…</p></main>`;

loadSnapshot()
  .then((value) => {
    snapshot = value;
    renderShell();
  })
  .catch((error: unknown) => {
    app.innerHTML = `<main class="fatal-state"><h1>Не вдалося завантажити звіт</h1><p>${escapeHtml(error instanceof Error ? error.message : 'Невідома помилка')}</p><button type="button" onclick="location.reload()">Спробувати ще раз</button></main>`;
  });
